import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { useCallback } from "react";
import { Router } from "wouter";
import { type ClaimResult, useAroundNavBoundary } from "../navigation/useAroundNavBoundary.ts";
import { sidebarContentForPath } from "./entityPathSidebar.ts";
import { useRightSidebar } from "./hooks.tsx";

/**
 * Region-scoped navigation-interception boundary for the right sidebar.
 *
 * Wraps its children in a nested wouter `<Router>` whose `aroundNav` turns a
 * navigation to a canonical entity page URL (e.g. `/contacts/:id`) into a
 * right-sidebar open on desktop, while declining — real full-page navigation —
 * on mobile or when the target has no sidebar representation. Callers link to
 * the canonical URL and never classify the surface themselves; the boundary
 * decides, and `<a href>` stays real so shareable links and cmd/middle-click
 * still open the full page.
 *
 * Mounted where the sidebar lives (in `ViewWrapper`, around
 * `RightSidebarProvider`) so it also covers links rendered inside sidebars.
 *
 * `openSidebar` here is bound to the *parent* router — this component renders
 * above its own `<Router>` — so its `sb_` query writes bypass this boundary's
 * `aroundNav` and can't recurse. Sidebar-internal writes (close button, swaps)
 * are bound to this nested router and DO reach `aroundNav`; they pass through
 * via the explicit `fullNavigation` state their `setLocation` calls carry.
 *
 * Declines delegate through the parent router's navigate (see
 * {@link useAroundNavBoundary}); calling wouter's raw `navigate` would skip any
 * outer boundary.
 */
export const SidebarNavBoundary = ({ children }: { children: React.ReactNode }) => {
  const { openSidebar } = useRightSidebar();
  const isMobile = useIsMobile();

  const claim = useCallback(
    (to: string): ClaimResult => {
      // Mobile and unmapped targets fall through to the canonical URL.
      if (isMobile) return "delegate";
      const content = sidebarContentForPath(to);
      if (!content) return "delegate";
      // Claim: open the sidebar instead of navigating. This write goes through the
      // parent router (see the note above), so it doesn't re-enter here.
      openSidebar(content);
      return "claimed";
    },
    [isMobile, openSidebar],
  );

  const aroundNav = useAroundNavBoundary(claim);
  return <Router aroundNav={aroundNav}>{children}</Router>;
};
